// src/aws-amplify-auth.jsx
// AWS Amplify Auth context to replace Auth0

import React, { useState, useEffect, useContext, createContext } from "react";
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth';
import { S3Client } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import awsConfig from './aws-config';
import { API_URL } from './config';
import { checkPortalAccess, shouldRedirectToStore } from './auth/portalAccess';

// Configure Amplify
Amplify.configure(awsConfig.amplifyConfig);

const DEFAULT_REDIRECT_CALLBACK = (appState) => {
  const targetUrl = (appState && appState.targetUrl) ? appState.targetUrl : "/dashboard";
  window.history.replaceState({}, document.title, targetUrl);
  window.location.pathname = targetUrl;
};

export const CognitoAuthContext = createContext();
export const useCognitoAuth = () => useContext(CognitoAuthContext);

export const CognitoAuthProvider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [token, setToken] = useState("");
  const [AWS, setAWS] = useState(null);
  const [s3Client, setS3Client] = useState(null);
  const [active, setActive] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [currentStorage, setCurrentStorage] = useState(0);
  const [maxStorage, setMaxStorage] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showFooter, setShowFooter] = useState(true);

  // Helper function to check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      // Parse JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      // Add 5 minute buffer before actual expiration
      return payload.exp < (currentTime + 300);
    } catch (error) {
      console.error('Error parsing token:', error);
      return true; // Assume expired if we can't parse it
    }
  };

  // Function to initialize token and AWS credentials
  const initializeTokenAndAWS = async () => {
    try {
      setTokenLoading(true);
      console.log('Starting AWS credential initialization...');

      // Get the current auth session
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      const idToken = session.tokens?.idToken?.toString();

      console.log('Session tokens obtained:', {
        hasAccessToken: !!accessToken,
        hasIdToken: !!idToken,
        accessTokenLength: accessToken?.length || 0,
        idTokenLength: idToken?.length || 0
      });

      // Decode and log token details for debugging
      if (idToken) {
        try {
          const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
          console.log('ID Token payload:', {
            iss: idTokenPayload.iss,
            aud: idTokenPayload.aud,
            token_use: idTokenPayload.token_use,
            exp: new Date(idTokenPayload.exp * 1000),
            iat: new Date(idTokenPayload.iat * 1000)
          });
        } catch (e) {
          console.warn('Could not decode ID token:', e);
        }
      }

      if (accessToken) {
        setToken(accessToken);
      }

      if (!idToken) {
        throw new Error('No ID token available for Cognito Identity Pool authentication');
      }

      const loginKey = `cognito-idp.${awsConfig.awsRegion}.amazonaws.com/${awsConfig.cognitoUserPoolId}`;
      console.log('Using login key for Identity Pool:', loginKey);
      console.log('Identity Pool ID:', awsConfig.cognitoIdentityPoolId);

      // Set up AWS credentials using Cognito Identity Pool
      const credentials = fromCognitoIdentityPool({
        identityPoolId: awsConfig.cognitoIdentityPoolId,
        logins: {
          [loginKey]: idToken,
        },
        clientConfig: { region: awsConfig.awsRegion },
      });

      console.log('Credentials provider created, testing credential resolution...');

      // Test credential resolution
      try {
        const testCreds = await credentials();
        console.log('Credentials resolved successfully:', {
          accessKeyId: testCreds.accessKeyId?.substring(0, 10) + '...',
          hasSecretAccessKey: !!testCreds.secretAccessKey,
          hasSessionToken: !!testCreds.sessionToken
        });
      } catch (credError) {
        console.error('Credential resolution failed:', credError);
        throw credError;
      }

      // Create S3 client with the credentials
      const s3ClientInstance = new S3Client({
        region: awsConfig.awsRegion,
        credentials: credentials,
      });

      console.log('S3 client created successfully');

      // Mock AWS object to maintain compatibility with existing code
      const AWSObject = {
        config: {
          credentials: credentials,
        },
      };

      setAWS(AWSObject);
      setS3Client(s3ClientInstance);
      setTokenLoading(false);
      console.log('AWS credential initialization completed successfully');
    } catch (error) {
      console.error("Error initializing token and AWS credentials:", error);
      console.error("Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setToken("");
      setTokenLoading(false);
      throw error; // Re-throw to let caller handle it
    }
  };

  // Function to fetch user profile and subscription data
  const fetchUserProfileAndSubscription = async (accessToken) => {
    try {
      setProfileLoading(true);
      console.log('Fetching user profile and subscription data...');

  // Single profile request (includes user/app metadata)
  const profileDataResponse = await fetch(`${API_URL}/users/v1/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: "details" })
      });

      let profileAppMetadata = null;
      if (profileDataResponse.ok) {
        const profileFullData = await profileDataResponse.json();
        console.log('User profile data with app_metadata:', profileFullData);

        // Handle both DynamoDB format and regular format
        if (profileFullData.app_metadata?.M) {
          // DynamoDB format - convert to regular format
          profileAppMetadata = {
            current: {
              storage: parseInt(profileFullData.app_metadata.M.current?.M?.storage?.N || '0'),
              emails: parseInt(profileFullData.app_metadata.M.current?.M?.emails?.N || '0'),
              photos: parseInt(profileFullData.app_metadata.M.current?.M?.photos?.N || '0')
            },
            limits: {
              storage: parseInt(profileFullData.app_metadata.M.limits?.M?.storage?.N || '0'),
              emails: parseInt(profileFullData.app_metadata.M.limits?.M?.emails?.N || '0'),
              photos: parseInt(profileFullData.app_metadata.M.limits?.M?.photos?.N || '0')
            },
            subscription: parseInt(profileFullData.app_metadata.M.subscription?.N || '0'),
            subscriptionId: profileFullData.app_metadata.M.subscriptionId?.S || '',
            accountType: profileFullData.app_metadata.M.accountType?.S || '',
            registered: profileFullData.app_metadata.M.registered?.BOOL || false,
            subEndDate: profileFullData.app_metadata.M.subEndDate?.S || '',
            // Newly surfaced flags
            storeEnabled: (profileFullData.app_metadata.M.storeEnabled?.BOOL !== undefined)
              ? profileFullData.app_metadata.M.storeEnabled.BOOL
              : (profileFullData.app_metadata.M.storeEnabled?.S === 'true'),
            payoutsEnabled: (profileFullData.app_metadata.M.payoutsEnabled?.BOOL !== undefined)
              ? profileFullData.app_metadata.M.payoutsEnabled.BOOL
              : (profileFullData.app_metadata.M.payoutsEnabled?.S === 'true')
          };
        } else {
          // Regular format
          profileAppMetadata = profileFullData.app_metadata || {};
        }

        console.log('Converted app_metadata:', profileAppMetadata);

        // Normalize linked account IDs regardless of storage shape
        const linkedAccountIdsRaw =
          profileAppMetadata?.linkedAccountIds ||
          profileFullData?.linkedAccountIds ||
          profileFullData?.app_metadata?.M?.linkedAccountIds?.L ||
          [];

        const normalizeLink = (entry) => {
          if (!entry) return null;
          if (typeof entry === 'string') {
            return { userId: entry, status: 'active' };
          }
          const userId = entry.userId || entry.userId?.S;
          if (!userId) return null;
          return {
            userId,
            status: entry.status || entry.status?.S || 'active',
            linkedAt: entry.linkedAt || entry.linkedAt?.S,
            initiatedBy: entry.initiatedBy || entry.initiatedBy?.S,
            type: entry.type || entry.type?.S || 'view',
          };
        };

        const linkedAccountIds = Array.isArray(linkedAccountIdsRaw)
          ? linkedAccountIdsRaw.map(normalizeLink).filter(Boolean)
          : [];

        // Mirror the shape in both app_metadata and as a top-level convenience key
        if (profileAppMetadata) {
          profileAppMetadata.linkedAccountIds = linkedAccountIds;
        }

        // Update user object with app_metadata from profile data
        setUser(prevUser => ({
          ...prevUser,
          user_metadata: {
            firstName: profileFullData.user_metadata?.firstName || prevUser?.user_metadata?.firstName || '',
            lastName: profileFullData.user_metadata?.lastName || prevUser?.user_metadata?.lastName || '',
            username: profileFullData.user_metadata?.username || prevUser?.user_metadata?.username || prevUser?.username || '',
            copyright: profileFullData.user_metadata?.copyright || prevUser?.user_metadata?.copyright || ''
          },
          linkedAccountIds,
          app_metadata: profileAppMetadata
        }));

        // Set admin status based on account type
        setIsAdmin(profileAppMetadata?.accountType === 'administrator');
        console.log('Admin status set:', profileAppMetadata?.accountType === 'administrator');
      }

      // Fetch subscription data for tier limits (fallback if profile doesn't have limits)
      const subscriptionResponse = await fetch(`${API_URL}/users/v1/subscribe`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (subscriptionResponse.ok) {
        const subscriptionData = await subscriptionResponse.json();
        console.log('User subscription data:', subscriptionData);

        // Extract current storage usage from profile data (preferred) or subscription data (fallback)
        const currentStorageUsage = profileAppMetadata?.current?.storage ||
          subscriptionData.app_metadata?.current?.storage || 0;

        // Extract max storage from profile limits (preferred) or use tier-based calculation
        let maxStorageLimit = profileAppMetadata?.limits?.storage || 0;

        if (!maxStorageLimit) {
          // Fallback: calculate from tier if limits not in profile
          const tierLimits = {
            0: { storage: 0 },
            1: { storage: 10737418240 }, // 10GB in Bytes
            2: { storage: 107374182400 }, // 100GB in Bytes
            3: { storage: 536870912000 }, // 500GB in Bytes
            4: { storage: 1610612736000 }, // 1500GB in Bytes
          };

          const userTier = profileAppMetadata?.subscription || subscriptionData.tier || 0;
          maxStorageLimit = tierLimits[userTier]?.storage || 0;
        }

        console.log('Setting storage from profile/subscription data:', {
          currentStorageUsage,
          maxStorageLimit,
          fromProfileCurrent: profileAppMetadata?.current?.storage,
          fromProfileLimits: profileAppMetadata?.limits?.storage,
          fromSubscription: subscriptionData.app_metadata?.current?.storage,
          userTier: profileAppMetadata?.subscription || subscriptionData.tier
        });

        setMaxStorage(maxStorageLimit);
        setCurrentStorage(currentStorageUsage);
      }
    } catch (error) {
      console.error('Error fetching user profile and subscription:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        setLoading(true);
        console.log('Checking authentication state...');

        // First check if we have a valid session - avoid forcing refresh unless necessary
        const session = await fetchAuthSession({ forceRefresh: false });

        if (session.tokens?.accessToken) {
          const accessTokenString = session.tokens.accessToken.toString();

          // Check if token is still valid before proceeding with full auth flow
          if (isTokenExpired(accessTokenString)) {
            console.log('Token expired, refreshing session...');
            // Token expired, try to refresh
            const refreshedSession = await fetchAuthSession({ forceRefresh: true });

            if (!refreshedSession.tokens?.accessToken) {
              throw new Error('Unable to refresh expired token');
            }
          }

          // User is authenticated, get their details
          const currentUser = await getCurrentUser();
          const userAttributes = await fetchUserAttributes();

          setIsAuthenticated(true);
          setActive(true);
          setUser({
            sub: currentUser.userId,
            email: userAttributes.email || currentUser.signInDetails?.loginId || '',
            email_verified: userAttributes.email_verified === 'true',
            name: userAttributes.email || currentUser.signInDetails?.loginId || '',
            picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(userAttributes.email || 'User')}&background=2997ff&color=fff`,
            user_metadata: {
              firstName: userAttributes.given_name || '',
              lastName: userAttributes.family_name || '',
              username: currentUser.username,
              copyright: ''
            }
          });

          // Check admin mode from localStorage
          const adminModeStorage = localStorage.getItem("adminMode");
          setAdminMode(adminModeStorage === "true");

          // Only initialize AWS credentials if needed (for S3 operations)
          // Skip this if Identity Pool has issues
          try {
            console.log('Using Identity Pool:', awsConfig.cognitoIdentityPoolId);

            await initializeTokenAndAWS();
          } catch (awsError) {
            console.warn("AWS credentials initialization failed (S3 operations may not work):", awsError);
            // Continue without AWS credentials - user is still authenticated for API calls
            // Get current session again in case it was refreshed
            const currentSession = await fetchAuthSession({ forceRefresh: false });
            setToken(currentSession.tokens.accessToken.toString());
            setTokenLoading(false);
          }

          // Fetch user profile and subscription data from your API
          // Get current session to ensure we have the latest token
          const currentSession = await fetchAuthSession({ forceRefresh: false });
          await fetchUserProfileAndSubscription(currentSession.tokens.accessToken.toString());
          setIsRegistered(true);
        } else {
          // User is not authenticated
          setIsAuthenticated(false);
          setActive(false);
          setUser(null);
          setTokenLoading(false);
          setProfileLoading(false);
        }
      } catch (error) {
        console.error("Error checking auth state:", error);
        // If there's an authentication error, try to clear any corrupted state
        if (error.name === 'InvalidIdentityPoolConfigurationException') {
          console.warn("Identity Pool configuration issue - clearing auth state");
          await cognitoSignOut();
        }
        setIsAuthenticated(false);
        setActive(false);
        setUser(null);
        setTokenLoading(false);
        setProfileLoading(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthState();
  }, []);

  // Sign in function
  const cognitoSignIn = async (username, password) => {
    try {
      // Check if user is already signed in
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          console.log("User already signed in, signing out first");
          await signOut();
        }
      } catch (error) {
        // User not signed in, proceed with sign in
      }

      const { isSignedIn, nextStep } = await signIn({ username, password });

      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        const baseUser = {
          sub: currentUser.userId,
          email: currentUser.signInDetails?.loginId || username,
          email_verified: false,
          name: currentUser.signInDetails?.loginId || username,
          picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.signInDetails?.loginId || username)}&background=2997ff&color=fff`,
          user_metadata: {
            firstName: '',
            lastName: '',
            username: currentUser.username,
            copyright: ''
          }
        };

        // Clear any stale user data from previous sessions before setting new user
        clearUserData();

        // Get basic User Pool session and user attributes WITHOUT triggering Identity Pool
        console.log('Getting User Pool session and attributes...');
        const basicSession = await fetchAuthSession({ forceRefresh: false });
        let nextUser = baseUser;

        // Get user attributes now that Identity Pool is not auto-triggered
        try {
          const userAttributes = await fetchUserAttributes();
          console.log('✅ User attributes obtained:', Object.keys(userAttributes));

          nextUser = {
            ...baseUser,
            email: userAttributes.email || baseUser.email,
            email_verified: userAttributes.email_verified === 'true',
            name: userAttributes.email || baseUser.name,
            picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(userAttributes.email || baseUser.name)}&background=2997ff&color=fff`,
            user_metadata: {
              firstName: userAttributes.given_name || '',
              lastName: userAttributes.family_name || '',
              username: currentUser.username,
              copyright: ''
            }
          };
        } catch (attrError) {
          console.warn("Could not fetch user attributes:", attrError);
        }

        if (basicSession.tokens?.accessToken) {
          setToken(basicSession.tokens.accessToken.toString());
          console.log('✅ Access token obtained for API calls');
        }

        setTokenLoading(false);

        // NOW attempt manual Identity Pool credential setup for S3 operations
        console.log('=== MANUAL IDENTITY POOL SETUP ===');
        console.log('AWS Config values:', {
          awsRegion: awsConfig.awsRegion,
          cognitoUserPoolId: awsConfig.cognitoUserPoolId,
          cognitoClientId: awsConfig.cognitoClientId,
          cognitoIdentityPoolId: awsConfig.cognitoIdentityPoolId
        });

        try {
          const idToken = basicSession.tokens?.idToken?.toString();
          if (!idToken) {
            throw new Error('No ID token available for Identity Pool');
          }

          // Decode and validate token
          const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
          console.log('ID Token details:', {
            iss: idTokenPayload.iss,
            aud: idTokenPayload.aud,
            token_use: idTokenPayload.token_use
          });

          const expectedIssuer = `https://cognito-idp.${awsConfig.awsRegion}.amazonaws.com/${awsConfig.cognitoUserPoolId}`;
          const expectedClientId = awsConfig.cognitoClientId;

          console.log('Expected issuer:', expectedIssuer);
          console.log('Expected client ID:', expectedClientId);

          if (idTokenPayload.iss !== expectedIssuer) {
            console.error('❌ Token issuer mismatch!');
            throw new Error(`Token issuer ${idTokenPayload.iss} does not match expected ${expectedIssuer}`);
          }

          if (idTokenPayload.aud !== expectedClientId) {
            console.error('❌ Token audience mismatch!');
            throw new Error(`Token audience ${idTokenPayload.aud} does not match expected ${expectedClientId}`);
          }

          console.log('✅ Token validation passed, creating Identity Pool credentials...');

          const loginKey = `cognito-idp.${awsConfig.awsRegion}.amazonaws.com/${awsConfig.cognitoUserPoolId}`;
          console.log('Login key:', loginKey);
          console.log('Identity Pool ID:', awsConfig.cognitoIdentityPoolId);
          console.log('AWS Region:', awsConfig.awsRegion);

          // Try with explicit region configuration
          const credentials = fromCognitoIdentityPool({
            identityPoolId: awsConfig.cognitoIdentityPoolId,
            logins: {
              [loginKey]: idToken,
            },
            clientConfig: { region: awsConfig.awsRegion },
          });

          console.log('Credential provider created with config:', {
            identityPoolId: awsConfig.cognitoIdentityPoolId,
            loginKey: loginKey,
            clientConfig: { region: awsConfig.awsRegion }
          });

          // Test credential resolution
          const resolvedCreds = await credentials();
          console.log('✅ Identity Pool credentials resolved!', {
            accessKeyId: resolvedCreds.accessKeyId?.substring(0, 10) + '...',
            hasSecretAccessKey: !!resolvedCreds.secretAccessKey,
            hasSessionToken: !!resolvedCreds.sessionToken
          });

          // Create S3 client
          const s3ClientInstance = new S3Client({
            region: awsConfig.awsRegion,
            credentials: credentials,
          });

          setAWS({ config: { credentials } });
          setS3Client(s3ClientInstance);
          console.log('✅ S3 client configured successfully');

        } catch (identityPoolError) {
          console.warn('🚨 Identity Pool setup failed (S3 uploads will not work):', identityPoolError);
          console.warn('This is OK - you can still use the app for non-S3 operations');

          if (identityPoolError.name === 'InvalidIdentityPoolConfigurationException') {
            console.error('🔍 Identity Pool Configuration Issues to Check:');
            console.error('1. In AWS Console → Cognito → Identity Pools → Your Pool → Authentication providers');
            console.error('2. Verify User Pool ID matches:', awsConfig.cognitoUserPoolId);
            console.error('3. Verify Client ID matches:', awsConfig.cognitoClientId);
            console.error('4. Verify Authenticated Role is assigned:', 'service-role/CognitoUserCredentials-Stage');
            console.error('5. Check IAM role trust policy includes your Identity Pool ID');
          }
        }

        // Enforce portal access policy (distributor/uploader/administrator only)
        // Refactored to use extracted pure helper (SS-411)
        try {
          const attrSet = await fetchUserAttributes();
          const acctType = (attrSet['custom:accountType'] || '').toLowerCase();
          const idTok = basicSession.tokens?.idToken?.toString();
          let groups = [];
          if (idTok) {
            try { groups = JSON.parse(atob(idTok.split('.')[1]))['cognito:groups'] || []; } catch { groups = []; }
          }
          const lg = groups.map(g => g.toLowerCase());

          const access = checkPortalAccess(acctType, lg);
          if (!access.allowed) {
            console.warn('Access denied for portal', { acctType, groups });
            await signOut();
            setIsAuthenticated(false);
            setActive(false);
            setUser(null);
            const redirectToStore = shouldRedirectToStore(acctType);
            return {
              success: false,
              error: access.reason,
              redirectToStore,
            };
          }
        } catch (policyErr) {
          console.warn('Policy enforcement failure', policyErr);
          await signOut();
          setIsAuthenticated(false);
          setActive(false);
          setUser(null);
          return { success: false, error: 'Authorization check failed.' };
        }

        setIsAuthenticated(true);
        setActive(true);
        setUser(nextUser);

        console.log('Authentication state set, user:', currentUser.userId);

        // Fetch user profile and subscription data after successful authentication & policy pass
        console.log('Fetching user profile and subscription data...');
        if (basicSession.tokens?.accessToken) {
          await fetchUserProfileAndSubscription(basicSession.tokens.accessToken.toString());
          setIsRegistered(true);
        }

        console.log('Sign-in process completed successfully');
        return { success: true };
      } else {
        return { success: false, nextStep };
      }
    } catch (error) {
      console.error("Sign in error:", error);
      return { success: false, error: error.message };
    }
  };

  // Sign up function
  const cognitoSignUp = async (email, password, attributes = {}) => {
    try {
      // Generate a unique username since we're using email alias
      // Use timestamp + random to ensure uniqueness
      const uniqueUsername = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Determine website based on environment - this validates local/staging access
      const deployEnv = import.meta.env.VITE_DEPLOY_ENV || 'development';
      const currentWebsite = deployEnv === 'production' ? 'https://shieldshot.com' :
        deployEnv === 'staging' ? 'https://staging.shieldshot.com' :
        deployEnv === 'beta' ? 'https://beta.shieldshot.com' : 'http://localhost:3000';

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: uniqueUsername,
        password,
        options: {
          userAttributes: {
            email: email,
            website: currentWebsite, // Required by the User Pool schema for environment validation
            // Include user profile data that will be used by Cognito post-confirmation trigger
            given_name: attributes.firstName || '',
            family_name: attributes.lastName || '',
            'custom:username': attributes.username || email.split('@')[0], // Use provided username or email prefix
            ...attributes
          }
        }
      });

      return { success: true, isSignUpComplete, userId, nextStep, username: uniqueUsername };
    } catch (error) {
      console.error("Sign up error:", error);
      return { success: false, error: error.message };
    }
  };

  // Confirm sign up
  const cognitoConfirmSignUp = async (username, code) => {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode: code
      });

      return { success: true, isSignUpComplete, nextStep };
    } catch (error) {
      console.error("Confirm sign up error:", error);
      return { success: false, error: error.message };
    }
  };

  // Refresh authentication session
  const refreshAuthSession = async () => {
    try {
      console.log('Refreshing authentication session...');

      // Force refresh the current session
      const session = await fetchAuthSession({ forceRefresh: true });

      if (session.tokens?.accessToken) {
        const newToken = session.tokens.accessToken.toString();
        setToken(newToken);
        console.log('✅ Authentication session refreshed successfully');

        // Update AWS credentials if they exist
        if (AWS && s3Client) {
          try {
            await initializeTokenAndAWS();
            console.log('✅ AWS credentials refreshed');
          } catch (awsError) {
            console.warn('Failed to refresh AWS credentials:', awsError);
          }
        }

        // Fetch user profile and subscription data
        if (session.tokens?.accessToken) {
          await fetchUserProfileAndSubscription(session.tokens.accessToken.toString());
        }

        return { success: true };
      } else {
        throw new Error('No access token received after refresh');
      }
    } catch (error) {
      console.error('Failed to refresh authentication session:', error);

      // If refresh fails, the user might need to sign in again
      if (error.name === 'NotAuthorizedException' || error.message.includes('refresh token')) {
        console.warn('Refresh token expired, user needs to sign in again');
        await cognitoSignOut();
      }

      return { success: false, error: error.message };
    }
  };

  // Clear all user-specific localStorage data
  const clearUserData = () => {
    console.log('Clearing user-specific localStorage data...');

    // Authentication-related
    localStorage.removeItem('adminMode');
    localStorage.removeItem('redirectAfterLogin');

    // Dashboard and main data
    localStorage.removeItem('dashboard');
    localStorage.removeItem('tierList');

    // Photosets data
    localStorage.removeItem('photosets');
    localStorage.removeItem('groupedSets');
    localStorage.removeItem('groups');
    localStorage.removeItem('photosetTiers');

    // Tiers data
    localStorage.removeItem('tiers');
    localStorage.removeItem('tierDropdownList');
    localStorage.removeItem('tierDropdownzList');

    // Admin data
    localStorage.removeItem('adminUsers');
    localStorage.removeItem('adminUsersLength');

    // Activities data
    localStorage.removeItem('activity');
    localStorage.removeItem('uniqueActivities');

    // Distributions data
    localStorage.removeItem('distributions');

    // Settings/profile data
    localStorage.removeItem('userProfile');
    localStorage.removeItem('templatesData');

    // Clear individual photoset cache (pattern: photoset_[id])
    // Clear user-specific cache (pattern: anything with user ID)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('photoset_') ||
        key.startsWith('dashboard_') ||
        key.startsWith('tierList_') ||
        key.startsWith('photosets_') ||
        key.startsWith('groupedSets_') ||
        key.startsWith('groups_') ||
        key.startsWith('photosetTiers_') ||
        key.startsWith('tiers_') ||
        key.startsWith('activity_') ||
        key.startsWith('uniqueActivities_') ||
        key.startsWith('distributions_')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    console.log(`Cleared ${keysToRemove.length + 9} localStorage items`);
  };

  // Sign out function
  const cognitoSignOut = async () => {
    try {
      console.log('Starting sign out process...');

      // Clear local state immediately to prevent UI flash during logout
      setIsAuthenticated(false);
      setActive(false);
      setUser(null);
      setToken("");
      setAWS(null);
      setS3Client(null);
      setTokenLoading(true); // Set to true to trigger loading state

      await signOut();
      console.log('Successfully signed out');

      // Clear all user-specific data
      clearUserData();

      // Redirect to home page after logout
      window.location.href = '/';

      return { success: true };
    } catch (error) {
      console.error("Sign out error:", error);
      // Even if sign out fails, clear local state and user data
      setIsAuthenticated(false);
      setActive(false);
      setUser(null);
      setToken("");
      setAWS(null);
      setS3Client(null);
      setTokenLoading(false);

      // Still clear user data even on error
      clearUserData();

      // Still redirect to home page
      window.location.href = '/';

      return { success: false, error: error.message };
    }
  };

  // Clear authentication state (for error recovery)
  const clearAuthState = async () => {
    try {
      await cognitoSignOut();
      // Force page reload to clear any cached state
      window.location.reload();
    } catch (error) {
      console.error("Error clearing auth state:", error);
      // Force reload anyway
      window.location.reload();
    }
  };

  // Reset password
  const cognitoResetPassword = async (username) => {
    try {
      const output = await resetPassword({ username });
      return { success: true, nextStep: output.nextStep };
    } catch (error) {
      console.error("Reset password error:", error);
      return { success: false, error: error.message };
    }
  };

  // Confirm reset password
  const cognitoConfirmResetPassword = async (username, code, newPassword) => {
    try {
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
      return { success: true };
    } catch (error) {
      console.error("Confirm reset password error:", error);
      return { success: false, error: error.message };
    }
  };

  // Legacy Auth0 compatibility methods
  const loginWithRedirect = async (options = {}) => {
    // For now, redirect to a custom login page
    // In production, you might want to use Cognito Hosted UI
    const targetUrl = options.appState?.targetUrl || window.location.pathname;
    localStorage.setItem('redirectAfterLogin', targetUrl);
    window.location.href = `/login${options.screen_hint === 'signup' ? '?mode=signup' : ''}`;
  };

  const getTokenSilently = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (error) {
      console.error("Error getting token silently:", error);
      throw error;
    }
  };

  const getIdTokenClaims = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (idToken) {
        return {
          __raw: idToken.toString(),
          ...idToken.payload
        };
      }
      throw new Error("No ID token available");
    } catch (error) {
      console.error("Error getting ID token claims:", error);
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    if (!user) return;
    try {
      const accessToken = await getTokenSilently();
      const profileResponse = await fetch(`${API_URL}/users/v1/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ type: 'details' }),
      });
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        const linkedAccountIdsRaw = profileData?.linkedAccountIds || [];
        const normalizeLink = (entry) => {
          if (!entry || typeof entry !== 'object') return null;
          return {
            userId: entry.userId || entry.id || '',
            linkedAt: entry.linkedAt || '',
            status: entry.status || 'pending',
            initiatedBy: entry.initiatedBy || '',
            email: entry.email || undefined,
            username: entry.username || undefined,
          };
        };
        const linkedAccountIds = Array.isArray(linkedAccountIdsRaw)
          ? linkedAccountIdsRaw.map(normalizeLink).filter(Boolean)
          : [];
        const profileAppMetadata = profileData?.app_metadata || {};
        profileAppMetadata.linkedAccountIds = linkedAccountIds;
        setUser(prevUser => ({
          ...prevUser,
          linkedAccountIds,
          app_metadata: profileAppMetadata,
        }));
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  return (
    <CognitoAuthContext.Provider
      value={{
        // Authentication state
        isAuthenticated,
        user,
        loading,
        tokenLoading,
        profileLoading,
        token,
        AWS,
        s3Client,
        active,
        isRegistered,
        adminMode,
        currentStorage,
        maxStorage,
        isAdmin,
        showFooter,

        // Cognito methods
        signIn: cognitoSignIn,
        signUp: cognitoSignUp,
        confirmSignUp: cognitoConfirmSignUp,
        signOut: cognitoSignOut,
        resetPassword: cognitoResetPassword,
        confirmResetPassword: cognitoConfirmResetPassword,
        refreshAuth: refreshAuthSession,
        clearAuthState, // For error recovery

        // Legacy Auth0 compatibility methods
        loginWithRedirect,
        logout: cognitoSignOut,
        getTokenSilently,
        getIdTokenClaims,
        refreshUserProfile,
        // Role helpers
        role: {
          accountType: user?.app_metadata?.accountType || '',
          isAdmin,
        }
      }}
    >
      {children}
    </CognitoAuthContext.Provider>
  );
};
